/**
 * The legal and finance form (F5c).
 *
 * Presentational: it takes the record and reports a save, so plan §3.1's
 * layering holds and the startup feature owns the query.
 *
 * **No bank fields** (PRD §5.11). There is no IBAN input, no account number,
 * nothing a payment could be made from — and `invoicingContact` is who to send
 * an invoice TO, which is what the workflow needs. A field here would be
 * payment data in a system with no payment controls.
 *
 * Every field saves independently and nothing is required. A finance team
 * fills this in over weeks from different sources, and a form that refuses to
 * save until it is complete gets filled in on paper instead — the readiness
 * panel above is how the gaps stay visible without blocking.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.9, PRD §5.11
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { LegalFinance, SaveLegalFinanceRequest } from '@nexus/contracts';

import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';

import {
  draftToPayload,
  type LegalFinanceDraft,
  ONBOARDING_STATUSES,
  TEXT_FIELDS,
  toDraft,
} from './legal-finance-fields';

export interface LegalFinanceFormProps {
  record: LegalFinance;
  isSaving: boolean;
  canEdit: boolean;
  onSave: (body: SaveLegalFinanceRequest) => void;
  /** A failure to report above the form. */
  error?: string | undefined;
}

export function LegalFinanceForm({
  record,
  isSaving,
  canEdit,
  onSave,
  error,
}: LegalFinanceFormProps): React.JSX.Element {
  const { t } = useTranslation();

  /**
   * Re-seeded when the record changes, without an effect.
   *
   * The obvious shape — `useEffect` calling `setDraft` when the version
   * changes — sets state synchronously during the effect and triggers a
   * cascading render, which React's lint rule flags and which shows up as a
   * flash of stale values after a save. Comparing the version during render
   * instead is React's documented pattern for adjusting state on a prop
   * change: it re-renders immediately, before anything is painted.
   *
   * Keyed on `version` AND `id`, because a first save changes the id from null
   * while the version stays at 0.
   */
  const [draft, setDraft] = useState<LegalFinanceDraft>(() => toDraft(record));
  const [seededFrom, setSeededFrom] = useState(
    `${record.id ?? ''}:${String(record.version)}`,
  );
  const recordKey = `${record.id ?? ''}:${String(record.version)}`;

  if (seededFrom !== recordKey) {
    setSeededFrom(recordKey);
    setDraft(toDraft(record));
  }

  const set = (key: keyof LegalFinanceDraft, value: string): void => {
    setDraft((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <form
      className="gap-s3 flex flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draftToPayload(draft, record.version));
      }}
    >
      {error === undefined ? null : (
        <p role="alert" className="text-sem-bad text-sm">
          {error}
        </p>
      )}

      {TEXT_FIELDS.map(({ key, max }) => (
        <Field key={key} label={t(`legalFinance.fields.${key}`)}>
          {({ id, ...described }) => (
            <Input
              id={id}
              value={draft[key]}
              maxLength={max}
              disabled={!canEdit}
              {...described}
              onChange={(event) => {
                set(key, event.target.value);
              }}
            />
          )}
        </Field>
      ))}

      {(['ndaSignedDate', 'masterAgreementDate'] as const).map((key) => (
        <Field key={key} label={t(`legalFinance.fields.${key}`)}>
          {({ id, ...described }) => (
            <Input
              id={id}
              type="date"
              value={draft[key]}
              disabled={!canEdit}
              {...described}
              onChange={(event) => {
                set(key, event.target.value);
              }}
            />
          )}
        </Field>
      ))}

      <Field label={t('legalFinance.fields.supplierOnboardingStatus')}>
        {({ id }) => (
          <Select
            id={id}
            value={draft.supplierOnboardingStatus}
            disabled={!canEdit}
            onValueChange={(value) => {
              set('supplierOnboardingStatus', value);
            }}
            options={ONBOARDING_STATUSES.map((status) => ({
              value: status,
              label: t(`legalFinance.status.${status}`),
            }))}
            ariaLabel={t('legalFinance.fields.supplierOnboardingStatus')}
          />
        )}
      </Field>

      {canEdit ? (
        <div>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? t('legalFinance.saving') : t('actions.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
